<script setup lang="ts">
import { NxtIcon } from '@future-harry/nxt-styles';
import { JobItem } from '@/modules/content-blocks/interfaces/jobs-block.interface';
import type { Category } from '@/core/collections/job-boards/job-board.interface';
import { useJobBoardStore } from '@/core/collections/job-boards/job-board.store';

const props = defineProps<{
   jobs: JobItem[];
   showSpontaneous?: boolean;
   spontaneousUrl?: string;
   viewMode: 'grid' | 'list';
   categories: Category[];
}>();

const jobBoardStore = useJobBoardStore();
const activeLang = computed(() => jobBoardStore.activeLanguageName);

function getImageForCategoryId(job: JobItem): string {
   const categoryId = job.profile?.profile_job_functions?.[0]?.job_function?.job_function_category?.sync_id;
   const category = props.categories.find((cat) => cat.sync_id === categoryId);

   return category?.image_url ? category.image_url : '/job-category-fallback.png';
}
</script>

<template>
   <section v-if="jobs.length" :class="['jobs-grid', { 'jobs-grid--list': viewMode === 'list' }]">
      <article v-for="job in jobs" :key="job.id" class="jobs-grid__item">
         <RouterLink :to="`/${job.sync_id}`">
            <div class="jobs-grid__media">
               <img :src="getImageForCategoryId(job)" />
            </div>
            <div class="jobs-grid__body">
               <h3>{{ job.title_translations[activeLang] || job.title }}</h3>
               <p>{{ job.city?.name }}</p>
               <span>
                  {{ $tb('live-editor.content-blocks.job_board.grid.job-link') }}
                  <NxtIcon name="arrow-right" size="small" />
               </span>
            </div>
         </RouterLink>
      </article>
      <article v-if="showSpontaneous" class="jobs-grid__item jobs-grid__item--spontaneous">
         <h3>{{ $tb('live-editor.content-blocks.job_board.spontaneous-apply.description') }}</h3>
         <a :href="spontaneousUrl">{{ $tb('live-editor.content-blocks.job_board.spontaneous-apply.action') }}</a>
      </article>
   </section>
   <section v-else>{{ $tb('live-editor.content-blocks.job_board.grid.empty') }}</section>
</template>

<style scoped lang="scss">
.jobs-grid {
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(20rem, 1fr));
   grid-auto-rows: min-content;
   gap: var(--nxt-gutter-large);

   &__item {
      display: flex;
      flex-direction: column;
      width: 100%;
      overflow: hidden;
      background-color: var(--jobsite-primary, var(--nxt-light-grey));
      border-radius: var(--jobsite-border-radius, var(--nxt-radius));

      &--spontaneous {
         padding: var(--nxt-gutter-xlarge);
         background: var(--nxt-grey);
         justify-content: space-between;

         h3 {
            margin: 0;
            font-size: var(--nxt-font-large);
         }
      }
   }

   &__media {
      height: 15rem;
      width: 100%;

      img {
         width: 100%;
         height: 100%;
         object-fit: cover;
         display: block;
      }
   }

   &__body {
      display: flex;
      flex-direction: column;
      gap: var(--nxt-gutter-small);
      padding: var(--nxt-gutter);

      h3 {
         margin: 0;
         font-size: var(--nxt-font-large);
         color: var(--jobsite-primary-font, var(--nxt-dark));
      }

      p {
         margin: 0;
         font-size: var(--nxt-font-small);
         color: var(--jobsite-primary-font, var(--nxt-dark));
      }

      span {
         margin-top: var(--nxt-gutter-small);
         font-size: var(--nxt-font-base);
         color: var(--jobsite-secondary-font, var(--nxt-blue));

         i {
            color: var(--jobsite-secondary-font, var(--nxt-blue));
            margin-left: var(--nxt-gutter-small);
         }
      }
   }

   &--list {
      display: flex;
      flex-direction: column;

      .jobs-grid__item {
         flex-direction: row;
      }

      .jobs-grid__media {
         width: 7rem;
         height: 7rem;
      }
   }
}
</style>
